import { mapGetters } from 'vuex'
import { postGoalReviewStreaming, stripMarkdownFences } from '@/contentprofile/api'
import { isCriteriaForGrade, isCriteriaForSubjects } from '@/util/goals'

export const autoReviewMixin = {
  data() {
    return {
      isAutoReviewModalOpen: false,
      autoReviewLoading: false,
      autoReviewError: null,
      autoReviewResult: null,
      objectivesReviewResult: null,
      autoReviewIsSavedGoal: false,
      autoReviewAbortController: null,
      autoReviewDismissed: false,
    }
  },
  computed: {
    ...mapGetters('contentProfile', [
      'profileId',
      'contentReviewCriteriaSetName',
      'contentReviewCriteria',
      'isCustomCriteriaSet'
    ]),
    ...mapGetters('visibility', ['betaVisibility']),
    hasObjectivesCriteria() {
      return this.contentReviewCriteria?.objectives && this.contentReviewCriteria.objectives.length > 0
    },
    filteredGoalsCriteria() {
      return this.getFilteredCriteria('goals')
    },
    filteredObjectivesCriteria() {
      return this.getFilteredCriteria('objectives')
    }
  },
  methods: {
    getFilteredCriteria(criteriaType) {
      const criteria = this.contentReviewCriteria?.[criteriaType]
      if (!criteria) return []

      const goalGrades = this.pageContent?.grades || []
      const goalSubjects = this.pageContent?.subject_alignments?.map(a => a.subject) || []

      return criteria.filter(criterion =>
        isCriteriaForGrade(criterion, goalGrades) || isCriteriaForSubjects(criterion, goalSubjects)
      )
    },
    getAutoReviewGoalText() {
      return this.pageContent?.goal_text || ''
    },
    getAutoReviewObjectiveTexts() {
      return this.editableObjectives ? [...this.editableObjectives] : []
    },
    getAutoReviewGoalUuid() {
      return this.pageContent?.uuid
    },
    openAutoReviewModal() {
      this.$sendEvent(this.autoReviewIsSavedGoal ? 'Clicked Saved Goal Auto-Review Button' : 'Clicked Goal Auto-Review Button')
      this.isAutoReviewModalOpen = true
    },
    closeAutoReviewModal() {
      this.isAutoReviewModalOpen = false
    },
    resetAutoReviewState() {
      this.autoReviewLoading = false
      this.autoReviewError = null
      this.autoReviewResult = null
      this.objectivesReviewResult = null
      if (this.autoReviewAbortController) {
        this.autoReviewAbortController.abort()
        this.autoReviewAbortController = null
      }
      this.autoReviewDismissed = false
    },
    async handleAutoReview() {
      this.$sendEvent(this.autoReviewIsSavedGoal ? 'Clicked Saved Auto-Review Goal Modal Review Button' : 'Clicked Auto-Review Goal Modal Review Button')
      this.autoReviewLoading = true
      this.autoReviewError = null
      this.autoReviewResult = null
      this.objectivesReviewResult = null
      this.autoReviewDismissed = false

      // Create a local controller to avoid race conditions with rapid successive calls
      const controller = new AbortController()
      this.autoReviewAbortController = controller

      try {
        const goalText = this.getAutoReviewGoalText()
        const objectiveTexts = this.getAutoReviewObjectiveTexts()
        const contentProfile = this.profileId
        const goalUuid = this.getAutoReviewGoalUuid()

        let lastGoalCount = 0
        let lastObjectiveCount = 0

        const result = await postGoalReviewStreaming({
          goalText,
          goalUuid,
          objectiveTexts,
          contentProfile,
          signal: controller.signal,
          betaMode: this.betaVisibility,
          onProgress: (partialContent) => {
            if (this.autoReviewDismissed) {
              console.log('[AUTO-REVIEW] Request was dismissed, skipping progress update')
              return
            }
            const cleanedContent = stripMarkdownFences(partialContent)
            if (!cleanedContent) return
            this.processPartialAutoReviewResults(cleanedContent, ({ goalResults, objectiveResults }) => {
              if (goalResults.length > lastGoalCount) {
                this.autoReviewResult = goalResults
                lastGoalCount = goalResults.length
              }
              if (objectiveResults.length > lastObjectiveCount) {
                this.objectivesReviewResult = { audit_results: objectiveResults }
                lastObjectiveCount = objectiveResults.length
              }
            })
          }
        })

        const auditResults = Array.isArray(result?.audit_results) ? result.audit_results : []
        const goalResults = auditResults.filter(r => r.section === 'goals')
        const objectiveResults = auditResults.filter(r => r.section === 'objectives')

        this.autoReviewResult = goalResults.length > 0 ? goalResults : null
        this.objectivesReviewResult = objectiveResults.length > 0 ? { audit_results: objectiveResults } : null

        this.$store.commit('goaldetail/setAutoReviewCompleted', true)

        this.$sendEvent('Received Auto-Review Goal Results', {
          goals_audit_scores: goalResults.map(r => ({ score: r.score, max_score: r.max_score, criteria_title: r.audit_title, section: r.section })) || [],
          objectives_audit_scores: objectiveResults.map(r => ({ score: r.score, max_score: r.max_score, criteria_title: r.audit_title, section: r.section })) || [],
          average_normalized_scores: this.calculateNormalizedScores(auditResults),
          is_custom_criteria_set: this.isCustomCriteriaSet,
        })
      } catch (error) {
        // Don't show error if the request was aborted by the user
        if (error.name === 'AbortError') {
          console.log('[AUTO-REVIEW] Request was aborted by user')
        } else {
          console.error('[AUTO-REVIEW] Error during auto-review:', error)
          this.autoReviewError = 'Unable to load goal review scores. Try again in a moment.'
        }
      } finally {
        // Only clear state if this is still the active controller (prevents race conditions)
        if (this.autoReviewAbortController === controller) {
          this.autoReviewLoading = false
          this.autoReviewAbortController = null
        }
      }
    },
    processPartialAutoReviewResults(cleanedContent, callback) {
      const auditResultsMatch = cleanedContent.match(/"audit_results"\s*:\s*\[/)
      if (!auditResultsMatch) return

      const arrayStartIndex = auditResultsMatch.index + auditResultsMatch[0].length
      const contentFromArray = cleanedContent.substring(arrayStartIndex)
      const extractedResults = []
      let depth = 0
      let currentObject = ''
      let inString = false
      let escapeNext = false

      for (let i = 0; i < contentFromArray.length; i++) {
        const char = contentFromArray[i]

        if (escapeNext) {
          currentObject += char
          escapeNext = false
          continue
        }

        if (char === '\\') {
          escapeNext = true
          currentObject += char
          continue
        }

        if (char === '"') {
          inString = !inString
          currentObject += char
          continue
        }

        if (!inString) {
          if (char === '{') {
            depth++
            currentObject += char
          } else if (char === '}') {
            currentObject += char
            depth--
            if (depth === 0 && currentObject.trim()) {
              try {
                const obj = JSON.parse(currentObject.trim())
                extractedResults.push(obj)
                currentObject = ''
              } catch (_e) {
                // swallow incomplete objects
              }
            }
          } else if (depth > 0) {
            currentObject += char
          }
        } else {
          currentObject += char
        }
      }

      if (extractedResults.length > 0) {
        const goalResults = extractedResults.filter(r => r.section === 'goals')
        const objectiveResults = extractedResults.filter(r => r.section === 'objectives')
        callback({ goalResults, objectiveResults })
      }
    },
    closeReviewResults() {
      this.$sendEvent(this.autoReviewIsSavedGoal ? 'Dismissed Saved Auto-Review Goal Results' : 'Dismissed Auto-Review Goal Results')
      this.autoReviewDismissed = true
      this.autoReviewResult = null
      this.autoReviewError = null
      this.autoReviewLoading = false

      // Abort the streaming request if it's still in progress
      if (this.autoReviewAbortController) {
        console.log('[AUTO-REVIEW] Aborting streaming request')
        this.autoReviewAbortController.abort()
        this.autoReviewAbortController = null
      }
    },
    closeObjectivesReviewResults() {
      this.$sendEvent(this.autoReviewIsSavedGoal ? 'Dismissed Saved Auto-Review Objectives Results' : 'Dismissed Auto-Review Objectives Results')
      this.autoReviewDismissed = true
      this.objectivesReviewResult = null
      this.autoReviewError = null
      this.autoReviewLoading = false

      // Abort the streaming request if it's still in progress
      if (this.autoReviewAbortController) {
        console.log('[AUTO-REVIEW] Aborting streaming request (objectives)')
        this.autoReviewAbortController.abort()
        this.autoReviewAbortController = null
      }
    },
    calculateNormalizedScores(detailedAuditScores) {
      const averageNormalizedScores = this.calculateNormalizedScoresIncremental(detailedAuditScores)
      for (const sectionKey of Object.keys(averageNormalizedScores)) {
        const section = averageNormalizedScores[sectionKey]
        const avg = section.count > 0 ? section.totalScore / section.count : 0
        section.averageScore = Number(avg.toFixed(2))
      }

      return averageNormalizedScores
    },
    calculateNormalizedScoresIncremental(detailedAuditScores) {
      const averageNormalizedScores = {}

      for (const score of detailedAuditScores) {
        const normalizedScore = (score.score / score.max_score) * 100
        if (!averageNormalizedScores[score.section]) {
          averageNormalizedScores[score.section] = {
            totalScore: 0,
            count: 0
          }
        }
        averageNormalizedScores[score.section].totalScore += normalizedScore
        averageNormalizedScores[score.section].count++
      }

      return averageNormalizedScores
    }
  }
}
